import { useState, useEffect } from 'react'
import { AlertTriangle, Shield, ArrowLeftRight, Truck, Package, Plus, Clock, CheckCircle, RefreshCw } from 'lucide-react'
import ReturnsListTab from '@/components/returns/ReturnsListTab'
import CreateIncidentModal from '@/components/incidents/CreateIncidentModal'
import WarrantyResultModal from '@/components/incidents/WarrantyResultModal'
import CloseIncidentModal from '@/components/incidents/CloseIncidentModal'
import CarrierClaimModal from '@/components/incidents/CarrierClaimModal'
import api from '@/lib/api'

type TabType = 'all' | 'warranty' | 'doa' | 'transport'

interface TabConfig {
  id: TabType
  label: string
  icon: React.ElementType
  caseType: string
  description: string
}

interface Stats {
  pending: number
  today: number
  total: number
  by_status: Record<string, number>
}

const TABS: TabConfig[] = [
  {
    id: 'all',
    label: 'Todas',
    icon: AlertTriangle,
    caseType: 'warranty,doa,transport',
    description: 'Todas las incidencias'
  },
  {
    id: 'warranty',
    label: 'Garantias',
    icon: Shield,
    caseType: 'warranty',
    description: 'Garantias de productos'
  },
  {
    id: 'doa',
    label: 'Cam-falla',
    icon: ArrowLeftRight,
    caseType: 'doa',
    description: 'Productos con fallo de fabrica'
  },
  {
    id: 'transport',
    label: 'Rotura/Perdida',
    icon: Truck,
    caseType: 'transport',
    description: 'Rotura o perdida en transporte'
  }
]

export default function IncidentsPage() {
  const [activeTab, setActiveTab] = useState<TabType>('all')
  const [stats, setStats] = useState<Stats | null>(null)
  const [createModalOpen, setCreateModalOpen] = useState(false)

  // Modal states for incident-specific actions
  const [warrantyResultModal, setWarrantyResultModal] = useState<{ open: boolean; returnId: number | null }>({ open: false, returnId: null })
  const [closeIncidentModal, setCloseIncidentModal] = useState<{ open: boolean; returnId: number | null; caseType: string }>({ open: false, returnId: null, caseType: '' })
  const [carrierClaimModal, setCarrierClaimModal] = useState<{ open: boolean; returnId: number | null }>({ open: false, returnId: null })

  useEffect(() => {
    loadStats()
  }, [])

  const loadStats = async () => {
    try {
      const response = await api.get('/returns/admin/stats?case_type=warranty,doa,transport')
      setStats(response.data)
    } catch (error) {
      console.error('Error loading incident stats:', error)
    }
  }

  const getTabCount = (tabId: TabType): number | undefined => {
    if (!stats) return undefined
    if (tabId === 'all') return stats.total
    return undefined // Individual tab counts would require per-type stats
  }

  const currentTab = TABS.find(t => t.id === activeTab)

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-10">
        <div className="px-6">
          <div className="py-4 border-b border-slate-100 dark:border-slate-700/50">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Incidencias</h1>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                  Garantias, cambios por fallo y roturas en transporte
                </p>
              </div>
              <button
                onClick={() => setCreateModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                Nueva incidencia
              </button>
            </div>

            {/* KPI Grid */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                    <span className="text-xs text-amber-700 dark:text-amber-300 font-medium">Pendientes</span>
                  </div>
                  <p className="text-xl font-bold text-amber-900 dark:text-amber-100 mt-1">{stats.pending}</p>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <span className="text-xs text-blue-700 dark:text-blue-300 font-medium">En proceso</span>
                  </div>
                  <p className="text-xl font-bold text-blue-900 dark:text-blue-100 mt-1">
                    {(stats.by_status?.approved || 0) + (stats.by_status?.pickup_scheduled || 0) + (stats.by_status?.in_transit || 0) + (stats.by_status?.received || 0) + (stats.by_status?.tested || 0)}
                  </p>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-green-600 dark:text-green-400" />
                    <span className="text-xs text-green-700 dark:text-green-300 font-medium">Reemplazos enviados</span>
                  </div>
                  <p className="text-xl font-bold text-green-900 dark:text-green-100 mt-1">
                    {(stats.by_status?.replacement || 0) + (stats.by_status?.warranty_approved || 0)}
                  </p>
                </div>
                <div className="bg-slate-50 dark:bg-slate-700/30 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                    <span className="text-xs text-slate-700 dark:text-slate-300 font-medium">Total incidencias</span>
                  </div>
                  <p className="text-xl font-bold text-slate-900 dark:text-slate-100 mt-1">{stats.total}</p>
                </div>
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 pt-2 overflow-x-auto scrollbar-hide">
            {TABS.map((tab) => {
              const Icon = tab.icon
              const isActive = activeTab === tab.id
              const count = getTabCount(tab.id)
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-3.5 py-2.5 border-b-2 transition-all whitespace-nowrap text-sm ${
                    isActive
                      ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400 font-semibold'
                      : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 font-medium'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? 'text-indigo-500' : ''}`} />
                  {tab.label}
                  {count !== undefined && count > 0 && (
                    <span className={`ml-0.5 px-1.5 py-0.5 text-[11px] font-semibold rounded-full leading-none ${
                      isActive
                        ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300'
                        : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="h-[calc(100vh-245px)]">
        <ReturnsListTab
          statusFilter={undefined}
          caseType={currentTab?.caseType}
          onStatsChange={loadStats}
        />
      </div>

      {/* Create Incident Modal */}
      {createModalOpen && (
        <CreateIncidentModal
          isOpen={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onSuccess={() => {
            setCreateModalOpen(false)
            loadStats()
          }}
        />
      )}

      {/* Warranty Result Modal */}
      {warrantyResultModal.open && warrantyResultModal.returnId && (
        <WarrantyResultModal
          isOpen={warrantyResultModal.open}
          returnId={warrantyResultModal.returnId}
          onClose={() => setWarrantyResultModal({ open: false, returnId: null })}
          onSuccess={() => {
            setWarrantyResultModal({ open: false, returnId: null })
            loadStats()
          }}
        />
      )}

      {/* Close Incident Modal */}
      {closeIncidentModal.open && closeIncidentModal.returnId && (
        <CloseIncidentModal
          isOpen={closeIncidentModal.open}
          returnId={closeIncidentModal.returnId}
          caseType={closeIncidentModal.caseType}
          onClose={() => setCloseIncidentModal({ open: false, returnId: null, caseType: '' })}
          onSuccess={() => {
            setCloseIncidentModal({ open: false, returnId: null, caseType: '' })
            loadStats()
          }}
        />
      )}

      {/* Carrier Claim Modal */}
      {carrierClaimModal.open && carrierClaimModal.returnId && (
        <CarrierClaimModal
          isOpen={carrierClaimModal.open}
          returnId={carrierClaimModal.returnId}
          onClose={() => setCarrierClaimModal({ open: false, returnId: null })}
          onSuccess={() => {
            setCarrierClaimModal({ open: false, returnId: null })
            loadStats()
          }}
        />
      )}
    </div>
  )
}
